import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  CalendarDays,
  ChevronDown,
  Copy,
  CirclePlus,
  Download,
  Gift,
  List,
  Save,
  ShoppingBag,
  Sparkles,
  Ticket,
  Trash2,
} from "lucide-react";

const API = process.env.REACT_APP_API_BASE_URL || "http://localhost:4242";
const activities = [
  ["pinta-tu-ceramica-granollers", "Pinta tu cerámica"],
  ["crea-tu-ceramica-y-decorala", "Crea tu cerámica y decórala"],
  ["ceramica-y-vino-granollers", "Cerámica + vino"],
  ["taller-ceramica-clases-granollers", "Clases semanales"],
];
const fallbackVoucherActivities = [
  {
    id: "pinta",
    name: "Pinta tu cerámica",
    detail: "Sesión creativa para pintar una pieza de cerámica.",
    conditions: ["Válido 6 meses desde emisión."],
  },
  {
    id: "dos-sesiones",
    name: "Crea tu cerámica y decórala",
    detail: "Dos sesiones: crear y decorar la pieza.",
    conditions: ["Válido 6 meses desde emisión."],
  },
  {
    id: "vino",
    name: "Cerámica + vino",
    detail: "Pintura cerámica con formato vino y picoteo.",
    conditions: ["Válido 6 meses desde emisión."],
  },
  {
    id: "clases",
    name: "Clases semanales · Pack de 4",
    detail: "Cuatro clases del pack semanal.",
    conditions: ["Válido 6 meses desde emisión."],
  },
];
const voucherTemplates = [
  { id: "lima", name: "Lima moderno", image: "/voucher-lima-modern.png" },
  { id: "floral", name: "Floral 2026", image: "/voucher-floral-2026.png" },
  { id: "azul-acuarela", name: "Fondo 1", image: "/assets/%20.jpg" },
  { id: "azul-aves", name: "Fondo 2", image: "/assets/%20-2.jpg" },
  { id: "rosa-peonias", name: "Fondo 3", image: "/assets/%20-3.jpg" },
  { id: "rosa-arco", name: "Fondo 4", image: "/assets/%20-4.jpg" },
  { id: "azul-arco", name: "Fondo 5", image: "/assets/%20-5.jpg" },
  { id: "azul-esquinas", name: "Fondo 6", image: "/assets/%20-6.jpg" },
];
const voucherThemes = [
  { id: "lima", name: "Lima" },
  { id: "terracota", name: "Terracota" },
  { id: "rosa", name: "Rosa" },
  { id: "azul", name: "Azul" },
];
const getAdminHeaders = (auth) => ({
  "Content-Type": "application/json",
  "X-Admin-Email": auth.email.toLowerCase(),
  Authorization: `Bearer ${encodeURIComponent(auth.password)}`,
});
const Page = styled.main`
  min-height: 100vh;
  padding: 8rem 1rem 6rem;
  background:
    radial-gradient(circle at 90% 4%, #dfff72 0, transparent 25%), #f4efe5;
  color: #173f35;
  font-family: "Inter", Arial, sans-serif;
`;
const Shell = styled.div`
  width: min(100%, 1100px);
  margin: auto;
`;
const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #cf7046;
  font-weight: 800;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;
const Title = styled.h1`
  max-width: 780px;
  margin: 0.7rem 0 1rem;
  font-family: "Fraunces", Georgia, serif;
  font-size: clamp(3rem, 8vw, 6.5rem);
  font-weight: 400;
  line-height: 0.86;
  letter-spacing: -0.06em;
`;
const Lead = styled.p`
  max-width: 680px;
  margin-bottom: 2rem;
  color: #5f665f;
  font-size: 1.05rem;
  line-height: 1.6;
`;
const Panel = styled.section`
  padding: 1.15rem;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #ded8ca;
  border-radius: 22px;
  box-shadow: 0 18px 45px rgba(23, 63, 53, 0.07);
  @media (min-width: 760px) {
    padding: 1.8rem;
  }
`;
const Grid = styled.div`
  display: grid;
  gap: 1rem;
  @media (min-width: 900px) {
    grid-template-columns: 1.4fr 0.8fr;
    align-items: start;
  }
`;
const Side = styled(Panel)`
  order: -1;
  @media (min-width: 900px) {
    order: 0;
    position: sticky;
    top: 100px;
  }
`;
const PanelTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0 0 0.4rem;
  font:
    400 clamp(1.7rem, 6vw, 2.2rem) "Fraunces",
    serif;
`;
const Hint = styled.p`
  margin: 0 0 1.2rem;
  color: #6b706a;
  line-height: 1.5;
`;
const Input = styled.input`
  width: 100%;
  min-height: 46px;
  padding: 0.7rem 0.8rem;
  border: 1px solid #d8d4ca;
  border-radius: 10px;
  background: #fff;
  font: 1rem/1.3 "Inter", Arial, sans-serif;
  color: #173f35;
`;
const Select = styled.select`
  width: 100%;
  min-height: 46px;
  padding: 0.7rem 0.8rem;
  border: 1px solid #d8d4ca;
  border-radius: 10px;
  background: #fff;
  font: 1rem/1.3 "Inter", Arial, sans-serif;
  color: #173f35;
`;
const Button = styled.button`
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border: 0;
  border-radius: 999px;
  background: #173f35;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;
const Soft = styled(Button)`
  background: #e7eddc;
  color: #173f35;
`;
const DeleteButton = styled(Soft)`
  width: 46px;
  min-width: 46px;
  padding: 0;
  justify-self: end;
  border-radius: 50%;
`;
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;
const Toggle = styled.div`
  display: flex;
  padding: 4px;
  border-radius: 999px;
  background: #edf0e7;
  button {
    min-height: 38px;
    padding: 0.5rem 0.8rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #173f35;
    font-weight: 700;
  }
  button.active {
    background: #173f35;
    color: white;
  }
`;
const Day = styled.article`
  margin: 0.65rem 0;
  border: 1px solid #ddd8cc;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
`;
const DayHead = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 1.05rem;
  border: 0;
  background: #fff;
  color: #173f35;
  text-align: left;
  cursor: pointer;
  > div {
    display: grid;
    gap: 0.28rem;
  }
  strong {
    font-size: 1.08rem;
    line-height: 1.15;
    text-transform: capitalize;
  }
  span {
    font-size: 0.8rem;
    color: #6b706a;
  }
  > span {
    display: none;
  }
  svg {
    transition: 0.2s;
    transform: rotate(${(p) => (p.$open ? "180deg" : "0")});
  }
  @media (min-width: 720px) {
    grid-template-columns: 1fr auto auto;
    > span { display: block; }
  }
`;
const Editor = styled.div`
  display: grid;
  gap: 0.75rem;
  padding: 0.8rem;
  background: #fbfaf6;
`;
const Slot = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.8rem;
  padding: 1rem;
  border: 1px solid ${({ $claimed }) => ($claimed ? "#badb45" : "#e1ded6")};
  border-radius: 20px;
  background: ${({ $claimed }) => ($claimed ? "#f3f9de" : "#fff")};
  box-shadow: ${({ $claimed }) =>
    $claimed ? "0 12px 30px rgba(63,89,34,.09)" : "none"};
  @media (min-width: 580px) {
    grid-template-columns: 150px 1fr auto;
    align-items: center;
  }
`;
const TimeInput = styled(Input)`
  min-height: 42px;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: "Fraunces", Georgia, serif;
  font-size: 1.75rem;
  letter-spacing: -0.04em;
  &:focus { outline: none; }
`;
const Switch = styled.label`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.76rem;
  font-weight: 800;
  cursor: pointer;
  input { position:absolute;opacity:0;pointer-events:none; }
  i {
    position: relative;
    width: 42px;
    height: 24px;
    border-radius: 999px;
    background: ${({ $on }) => ($on ? "#173f35" : "#d9d9d2")};
    transition: .2s;
  }
  i::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? "21px" : "3px")};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ $on }) => ($on ? "#dfff72" : "#fff")};
    transition: .2s;
  }
`;
const SlotMeta = styled.div`
  grid-column: 1/-1;
  display: grid;
  gap: 0.65rem;
  color: #5c685f;
  font-size: 0.78rem;
  @media (min-width: 580px) {
    grid-column: 2/4;
  }
`;
const MetaTop = styled.div`
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:.6rem;
  flex-wrap:wrap;
`;
const ActivityChip = styled.span`
  padding:.5rem .7rem;
  border-radius:999px;
  background:${({ $claimed }) => ($claimed ? "#dfff72" : "#f0eee8")};
  color:#173f35;
  font-weight:800;
`;
const Capacity = styled.strong`
  color:#173f35;
  font-size:.82rem;
`;
const CapacityBar = styled.div`
  height:7px;
  overflow:hidden;
  border-radius:999px;
  background:#e5e6df;
  span{display:block;height:100%;border-radius:inherit;background:#cf7046;transition:width .3s;}
`;
const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.65rem;
  @media (min-width: 560px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const CalendarDay = styled.button`
  min-height: 130px;
  padding: 0.9rem;
  border: 1px solid #dcd7ca;
  border-radius: 16px;
  background: #fff;
  color: #173f35;
  text-align: left;
  cursor: pointer;
  strong,
  span {
    display: block;
  }
  span {
    margin-top: 0.4rem;
    color: #6b706a;
    font-size: 0.8rem;
  }
`;
const CalendarSlots = styled.div`
  display: grid;
  gap: 0.35rem;
  margin-top: 0.65rem;
`;
const CalendarSlot = styled.div`
  padding: 0.45rem 0.55rem;
  border-radius: 9px;
  background: ${({ $closed, $full }) =>
    $closed ? "#eeeae2" : $full ? "#f8d8d0" : "#edf5d7"};
  font-size: 0.72rem;
  line-height: 1.35;
  strong {
    font-family: Arial, sans-serif;
    font-size: 0.76rem;
  }
`;
const Form = styled.form`
  display: grid;
  gap: 0.75rem;
`;
const Login = styled(Form)`
  max-width: 460px;
  padding: 1.2rem;
  background: #fff;
  border-radius: 20px;
`;
const Message = styled.p`
  margin-top: 1rem;
  padding: 0.8rem;
  border-radius: 12px;
  background: #eef5d4;
`;
const Divider = styled.hr`
  margin: 1.25rem 0;
  border: 0;
  border-top: 1px solid #e3dece;
`;
const SectionLabel = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.06rem;
`;
const Small = styled.p`
  margin: 0;
  color: #697068;
  font-size: 0.85rem;
  line-height: 1.45;
`;
const VoucherPreview = styled.article`
  display: grid;
  gap: 0.65rem;
  padding: 0.95rem;
  border: 1px solid #ddd8cb;
  border-radius: 16px;
  background: #fff;
`;
const TemplateImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 7;
  object-fit: cover;
  object-position: center;
  border-radius: 16px;
  border: 1px solid #ded8ca;
  display: block;
`;
const GeneratorForm = styled(Form)`
  gap: 0.85rem;
`;
const DesignGrid = styled.div`
  display: grid;
  gap: 0.7rem;
  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;
const FieldLabel = styled.label`
  display: grid;
  gap: 0.35rem;
  color: #687066;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;
const PreviewDetails = styled.details`
  border-top: 1px solid #e7e1d5;
  padding-top: 0.7rem;
  summary {
    color: #173f35;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 800;
  }
  ul, div { margin-top: 0.65rem; }
`;
const Conditions = styled.ul`
  margin: 0;
  padding-left: 1.2rem;
  color: #4f5a52;
  font-size: 0.84rem;
  line-height: 1.5;
`;
const Contacts = styled.div`
  display: grid;
  gap: 0.25rem;
  color: #5a655d;
  font-size: 0.78rem;
`;
const VoucherList = styled.div`
  display: grid;
  gap: 0.6rem;
  margin-top: 0.7rem;
`;
const VoucherItem = styled.article`
  padding: 0.8rem;
  border-radius: 14px;
  border: 1px solid #e1dccf;
  background: #fff;
`;
const VoucherTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
`;
const VoucherCode = styled.strong`
  font-size: 0.92rem;
  letter-spacing: 0.04em;
`;
const Badge = styled.span`
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  background: ${({ $status }) =>
    $status === "redeemed"
      ? "#dbf3bb"
      : $status === "expired"
        ? "#f8ddd5"
        : "#e7eddc"};
  color: #173f35;
`;
const VoucherMeta = styled.p`
  margin: 0.45rem 0 0;
  color: #5f665f;
  font-size: 0.78rem;
  line-height: 1.45;
`;
const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.55rem;
`;
const GhostButton = styled.button`
  min-height: 34px;
  padding: 0.4rem 0.65rem;
  border-radius: 999px;
  border: 1px solid #d9d4c7;
  background: #fff;
  color: #173f35;
  font-weight: 700;
  font-size: 0.73rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
`;

export default function VoucherAdmin() {
  const [auth, setAuth] = useState({ email: "", password: "" });
  const [slots, setSlots] = useState([]);
  const [occupied, setOccupied] = useState({});
  const [logged, setLogged] = useState(false);
  const [message, setMessage] = useState("");
  const [view, setView] = useState("list");
  const [openDate, setOpenDate] = useState("");
  const [manual, setManual] = useState({
    slot: "",
    slug: activities[0][0],
    personas: "1",
    customerName: "",
  });
  const [voucherForm, setVoucherForm] = useState({
    voucherId: "pinta",
    recipientName: "",
    dedication: "",
    signature: "",
    email: "",
    language: "es",
    template: "lima",
    theme: "lima",
    personas: "1",
  });
  const [voucherActivities, setVoucherActivities] = useState(
    fallbackVoucherActivities,
  );
  const [vouchers, setVouchers] = useState([]);
  const [creatingVoucher, setCreatingVoucher] = useState(false);
  const selectedVoucher =
    voucherActivities.find((item) => item.id === voucherForm.voucherId) ||
    voucherActivities[0];
  const statusLabel = {
    available: "Activo",
    redeemed: "Canjeado",
    expired: "Caducado",
    cancelled: "Cancelado",
  };
  const activityNameById = {
    pinta: "Pinta tu cerámica",
    "dos-sesiones": "Crea tu cerámica y decórala",
    vino: "Cerámica + vino",
    clases: "Clases semanales",
  };
  const load = async () => {
    const [scheduleResponse, vouchersResponse, productsResponse] = await Promise.all([
      fetch(`${API}/api/admin/voucher-schedule`, { headers: getAdminHeaders(auth) }),
      fetch(`${API}/api/admin/vouchers`, { headers: getAdminHeaders(auth) }),
      fetch(
        `${API}/api/admin/voucher-products?lang=${voucherForm.language}`,
        { headers: getAdminHeaders(auth) },
      ),
    ]);
    const scheduleData = await scheduleResponse.json();
    if (!scheduleResponse.ok) throw new Error(scheduleData.error);
    setSlots(scheduleData.slots || []);
    setOccupied(scheduleData.occupied || {});

    const voucherData = await vouchersResponse.json();
    if (!vouchersResponse.ok) throw new Error(voucherData.error);
    setVouchers(voucherData.items || []);

    const productsData = await productsResponse.json();
    if (!productsResponse.ok) throw new Error(productsData.error);
    if (Array.isArray(productsData.items) && productsData.items.length) {
      setVoucherActivities(productsData.items);
    }
  };
  const login = async (e) => {
    e.preventDefault();
    try {
      await load();
      setLogged(true);
      setMessage("");
    } catch (err) {
      setMessage(err.message);
    }
  };
  const save = async () => {
    const r = await fetch(`${API}/api/admin/voucher-schedule`, {
      method: "PUT",
      headers: getAdminHeaders(auth),
      body: JSON.stringify({ slots }),
    });
    const d = await r.json();
    if (!r.ok) return setMessage(d.error);
    setSlots(d.slots);
    setMessage("Agenda guardada.");
  };
  const addDate = () => {
    const date = new Date().toISOString().slice(0, 10);
    setSlots([...slots, { date, time: "17:00", enabled: true }]);
    setOpenDate(date);
    setView("list");
  };
  const addTime = (date) =>
    setSlots([...slots, { date, time: "17:00", enabled: true }]);
  const update = (index, changes) =>
    setSlots(
      slots.map((slot, i) => (i === index ? { ...slot, ...changes } : slot)),
    );
  const remove = (index) => setSlots(slots.filter((_, i) => i !== index));
  const sell = async (e) => {
    e.preventDefault();
    const [date, time] = manual.slot.split("|");
    const name = activities.find((a) => a[0] === manual.slug)?.[1];
    const r = await fetch(`${API}/api/admin/manual-booking`, {
      method: "POST",
      headers: getAdminHeaders(auth),
      body: JSON.stringify({ ...manual, date, time, activityName: name }),
    });
    const d = await r.json();
    if (!r.ok) return setMessage(d.error);
    setMessage(`Entradas registradas. Quedan ${d.remaining} plazas.`);
    await load();
  };
  const createVoucher = async (e) => {
    e.preventDefault();
    setCreatingVoucher(true);
    setMessage("");
    try {
      const r = await fetch(`${API}/api/admin/vouchers`, {
        method: "POST",
        headers: getAdminHeaders(auth),
        body: JSON.stringify({
          voucherId: voucherForm.voucherId,
          recipientName: voucherForm.recipientName,
          message: voucherForm.dedication,
          signature: voucherForm.signature,
          giverName: voucherForm.signature,
          email: voucherForm.email,
          language: voucherForm.language,
          template: voucherForm.template,
          theme: voucherForm.theme,
          personas: voucherForm.personas,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "No se pudo crear el voucher.");
      setMessage(`Voucher creado: ${d.item.code}`);
      setVoucherForm({
        ...voucherForm,
        recipientName: "",
        dedication: "",
        signature: "",
        email: "",
        personas: "1",
      });
      await load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setCreatingVoucher(false);
    }
  };
  const downloadVoucherPdf = async (code) => {
    try {
      const r = await fetch(`${API}/api/admin/vouchers/${code}/pdf`, {
        headers: getAdminHeaders(auth),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.error || "No se pudo generar PDF.");
      }
      const blob = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (err) {
      setMessage(err.message);
    }
  };
  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setMessage(`Código copiado: ${code}`);
    } catch {
      setMessage("No se pudo copiar el código automáticamente.");
    }
  };
  useEffect(() => {
    if (!logged) return;
    fetch(`${API}/api/admin/voucher-products?lang=${voucherForm.language}`, {
      headers: getAdminHeaders(auth),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) return;
        if (Array.isArray(data.items) && data.items.length) {
          setVoucherActivities(data.items);
        }
      })
      .catch(() => {});
  }, [logged, voucherForm.language, auth]);
  const days = useMemo(
    () => [...new Set(slots.map((s) => s.date))].sort(),
    [slots],
  );
  const totals = useMemo(
    () =>
      Object.values(occupied).reduce((sum, v) => sum + (v.personas || 0), 0),
    [occupied],
  );
  const renderDay = (date) => {
    const daySlots = slots
      .map((slot, index) => ({ ...slot, index }))
      .filter((s) => s.date === date);
    const used = daySlots.reduce(
      (sum, s) => sum + (occupied[`${s.date}|${s.time}`]?.personas || 0),
      0,
    );
    const open = daySlots.filter((s) => s.enabled).length;
    return (
      <Day key={date}>
        <DayHead
          type="button"
          $open={openDate === date}
          onClick={() => setOpenDate(openDate === date ? "" : date)}
        >
          <div>
            <strong>
              {new Date(`${date}T12:00:00`).toLocaleDateString("es-ES", {
                weekday: "short",
                day: "2-digit",
                month: "short",
              })}
            </strong>
            <span>
              {open} horarios abiertos · {used} ocupadas
            </span>
          </div>
          <span>{date}</span>
          <ChevronDown />
        </DayHead>
        {openDate === date && (
          <Editor>
            {daySlots.map((slot) => {
              const use = occupied[`${slot.date}|${slot.time}`];
              return (
                <Slot key={slot.index} $claimed={!!use}>
                  <TimeInput
                    type="time"
                    value={slot.time}
                    onChange={(e) =>
                      update(slot.index, { time: e.target.value })
                    }
                  />
                  <Switch $on={slot.enabled}>
                    <input
                      type="checkbox"
                      checked={slot.enabled}
                      onChange={(e) =>
                        update(slot.index, { enabled: e.target.checked })
                      }
                    />
                    <i aria-hidden="true" />
                    {slot.enabled ? "Abierto" : "Cerrado"}
                  </Switch>
                  <DeleteButton type="button" aria-label="Eliminar horario" onClick={() => remove(slot.index)}>
                    <Trash2 size={16} />
                  </DeleteButton>
                  <SlotMeta>
                    <MetaTop>
                      <ActivityChip $claimed={!!use}>
                        {activities.find(([slug]) => slug === use?.slug)?.[1] ||
                          "Todas las actividades"}
                      </ActivityChip>
                      <Capacity>{9 - (use?.personas || 0)} plazas libres</Capacity>
                    </MetaTop>
                    <CapacityBar aria-label={`${use?.personas || 0} de 9 plazas ocupadas`}>
                      <span style={{ width: `${((use?.personas || 0) / 9) * 100}%` }} />
                    </CapacityBar>
                  </SlotMeta>
                </Slot>
              );
            })}
            <Soft type="button" onClick={() => addTime(date)}>
              <CirclePlus size={16} /> Añadir horario
            </Soft>
          </Editor>
        )}
      </Day>
    );
  };
  return (
    <Page>
      <Shell>
        <Eyebrow>
          <Sparkles size={15} /> El Taller · Gestión
        </Eyebrow>
        <Title>Agenda y reservas</Title>
        <Lead>
          Una sola agenda para compras online, vouchers y ventas manuales. Una
          actividad por turno, hasta 9 personas.
        </Lead>
        {!logged ? (
          <Login onSubmit={login}>
            <Input
              type="email"
              placeholder="Correo administrador"
              required
              value={auth.email}
              onChange={(e) => setAuth({ ...auth, email: e.target.value })}
            />
            <Input
              type="password"
              placeholder="Contraseña"
              required
              value={auth.password}
              onChange={(e) => setAuth({ ...auth, password: e.target.value })}
            />
            <Button>Entrar</Button>
            {message && <Message>{message}</Message>}
          </Login>
        ) : (
          <Grid>
            <Panel>
              <PanelTitle>
                <CalendarDays /> Calendario
              </PanelTitle>
              <Hint>
                {days.length} fechas · {slots.length} horarios · {totals} plazas
                ocupadas.
              </Hint>
              <Toolbar>
                <Toggle>
                  <button
                    className={view === "list" ? "active" : ""}
                    onClick={() => setView("list")}
                  >
                    <List size={15} /> Lista
                  </button>
                  <button
                    className={view === "calendar" ? "active" : ""}
                    onClick={() => setView("calendar")}
                  >
                    <CalendarDays size={15} /> Calendario
                  </button>
                </Toggle>
                <div style={{ display: "flex", gap: 6 }}>
                  <Soft onClick={addDate}>
                    <CirclePlus size={16} /> Fecha
                  </Soft>
                  <Button onClick={save}>
                    <Save size={16} /> Guardar
                  </Button>
                </div>
              </Toolbar>
              {view === "list" ? (
                days.map(renderDay)
              ) : (
                <CalendarGrid>
                  {days.map((date) => {
                    const dateSlots = slots.filter((s) => s.date === date);
                    const count = dateSlots.filter((s) => s.enabled).length;
                    return (
                      <CalendarDay
                        key={date}
                        onClick={() => {
                          setOpenDate(date);
                          setView("list");
                        }}
                      >
                        <strong>
                          {new Date(`${date}T12:00:00`).toLocaleDateString(
                            "es-ES",
                            { day: "2-digit", month: "short" },
                          )}
                        </strong>
                        <span>{count} horarios · tocar para editar</span>
                        <CalendarSlots>
                          {dateSlots.map((slot) => {
                            const use = occupied[`${slot.date}|${slot.time}`];
                            const activity = activities.find(
                              ([slug]) => slug === use?.slug,
                            )?.[1];
                            const remaining = Math.max(
                              0,
                              9 - (use?.personas || 0),
                            );
                            return (
                              <CalendarSlot
                                key={slot.time}
                                $closed={!slot.enabled}
                                $full={remaining === 0}
                              >
                                <strong>{slot.time}</strong> · {remaining} libres
                                <br />
                                {slot.enabled
                                  ? activity || "Todas las actividades"
                                  : "Cerrado"}
                              </CalendarSlot>
                            );
                          })}
                        </CalendarSlots>
                      </CalendarDay>
                    );
                  })}
                </CalendarGrid>
              )}
            </Panel>
            <Side>
              <PanelTitle>
                <ShoppingBag /> Restar aforo
              </PanelTitle>
              <Hint>
                Registra ventas de otros canales y descuéntalas inmediatamente.
              </Hint>
              <Form onSubmit={sell}>
                <Select
                  required
                  value={manual.slot}
                  onChange={(e) =>
                    setManual({ ...manual, slot: e.target.value })
                  }
                >
                  <option value="">Elegir turno</option>
                  {slots
                    .filter((s) => s.enabled)
                    .map((s) => (
                      <option
                        key={`${s.date}|${s.time}`}
                        value={`${s.date}|${s.time}`}
                      >
                        {s.date} · {s.time} ·{" "}
                        {occupied[`${s.date}|${s.time}`]?.personas || 0}/9
                      </option>
                    ))}
                </Select>
                <Select
                  value={manual.slug}
                  onChange={(e) =>
                    setManual({ ...manual, slug: e.target.value })
                  }
                >
                  {activities.map((a) => (
                    <option key={a[0]} value={a[0]}>
                      {a[1]}
                    </option>
                  ))}
                </Select>
                <Select
                  value={manual.personas}
                  onChange={(e) =>
                    setManual({ ...manual, personas: e.target.value })
                  }
                >
                  {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? "entrada" : "entradas"}
                    </option>
                  ))}
                </Select>
                <Input
                  placeholder="Nombre (opcional)"
                  value={manual.customerName}
                  onChange={(e) =>
                    setManual({ ...manual, customerName: e.target.value })
                  }
                />
                <Button>
                  <Ticket size={16} /> Registrar entradas
                </Button>
              </Form>
              <Divider />
              <PanelTitle>
                <Gift /> Generador de voucher
              </PanelTitle>
              <Hint>
                Usa la plantilla oficial, elige actividad y genera un código
                único con estado activo para canjear más adelante.
              </Hint>
              <TemplateImage
                src={voucherTemplates.find((item) => item.id === voucherForm.template)?.image}
                alt={`Fondo ${voucherTemplates.find((item) => item.id === voucherForm.template)?.name || "oficial"} del voucher`}
                loading="lazy"
              />
              <GeneratorForm onSubmit={createVoucher}>
                <DesignGrid>
                  <FieldLabel>Fondo
                    <Select value={voucherForm.template} onChange={(e) => setVoucherForm({ ...voucherForm, template: e.target.value })}>
                      {voucherTemplates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </Select>
                  </FieldLabel>
                  <FieldLabel>Color de acento
                    <Select value={voucherForm.theme} onChange={(e) => setVoucherForm({ ...voucherForm, theme: e.target.value })}>
                      {voucherThemes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </Select>
                  </FieldLabel>
                </DesignGrid>
                <FieldLabel>Experiencia
                  <Select value={voucherForm.voucherId} onChange={(e) => { const voucherId = e.target.value; setVoucherForm({ ...voucherForm, voucherId, personas: voucherId === "vino" && Number(voucherForm.personas) < 4 ? "4" : voucherForm.personas }); }}>
                    {voucherActivities.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </Select>
                </FieldLabel>
                <FieldLabel>Válido para
                  <Select value={voucherForm.personas} onChange={(e) => setVoucherForm({ ...voucherForm, personas: e.target.value })}>
                    {Array.from({ length: 9 }, (_, index) => index + 1).filter((count) => voucherForm.voucherId !== "vino" || count >= 4).map((count) => <option key={count} value={count}>{count} {count === 1 ? "persona" : "personas"}</option>)}
                  </Select>
                </FieldLabel>
                <FieldLabel>Para quién
                  <Input placeholder="Nombre de la persona" value={voucherForm.recipientName} onChange={(e) => setVoucherForm({ ...voucherForm, recipientName: e.target.value })} />
                </FieldLabel>
                <DesignGrid>
                  <FieldLabel>Firma <Input placeholder="Opcional" value={voucherForm.signature} onChange={(e) => setVoucherForm({ ...voucherForm, signature: e.target.value })} /></FieldLabel>
                  <FieldLabel>Email <Input placeholder="Opcional" type="email" value={voucherForm.email} onChange={(e) => setVoucherForm({ ...voucherForm, email: e.target.value })} /></FieldLabel>
                </DesignGrid>
                <FieldLabel>Dedicatoria <Input placeholder="Opcional" value={voucherForm.dedication} onChange={(e) => setVoucherForm({ ...voucherForm, dedication: e.target.value })} /></FieldLabel>
                <FieldLabel>Idioma
                  <Select value={voucherForm.language} onChange={(e) => setVoucherForm({ ...voucherForm, language: e.target.value })}>
                    <option value="es">Castellano</option><option value="ca">Català</option>
                  </Select>
                </FieldLabel>
                <VoucherPreview>
                  <SectionLabel>{selectedVoucher.name}</SectionLabel>
                  <Small>{selectedVoucher.detail}</Small>
                  <PreviewDetails>
                    <summary>Ver condiciones y datos de contacto</summary>
                    <Conditions>{selectedVoucher.conditions.map((condition) => <li key={condition}>{condition}</li>)}</Conditions>
                    <Contacts><strong>El Taller de Aguaymanto</strong><span>Urbanización Les Roquetes, Vilanova del Vallès · a 8 min de Granollers</span><span>+34 683 704 011 · info@aguaymanto.es</span></Contacts>
                  </PreviewDetails>
                </VoucherPreview>
                <Button disabled={creatingVoucher}>
                  <Ticket size={16} />
                  {creatingVoucher ? "Generando..." : "Generar voucher"}
                </Button>
              </GeneratorForm>
              <Divider />
              <SectionLabel>Vouchers creados</SectionLabel>
              <VoucherList>
                {vouchers.slice(0, 14).map((item) => (
                  <VoucherItem key={item.code}>
                    <VoucherTop>
                      <VoucherCode>{item.code}</VoucherCode>
                      <Badge $status={item.status}>
                        {statusLabel[item.status] || item.status}
                      </Badge>
                    </VoucherTop>
                    <VoucherMeta>
                      {activityNameById[item.voucherId] || item.voucherId} · {" "}
                      {item.recipientName || "Sin nombre"}
                    </VoucherMeta>
                    <VoucherMeta>
                      {item.source === "admin"
                        ? "Creado en admin"
                        : "Creado por compra online"}
                    </VoucherMeta>
                    <Actions>
                      <GhostButton
                        type="button"
                        onClick={() => copyCode(item.code)}
                      >
                        <Copy size={14} /> Copiar código
                      </GhostButton>
                      <GhostButton
                        type="button"
                        onClick={() => downloadVoucherPdf(item.code)}
                      >
                        <Download size={14} /> PDF
                      </GhostButton>
                    </Actions>
                  </VoucherItem>
                ))}
              </VoucherList>
              {message && <Message>{message}</Message>}
            </Side>
          </Grid>
        )}
      </Shell>
    </Page>
  );
}
